import { useEffect, useRef, useState } from "react";
import type React from "react";
import { api } from "../lib/api";
import { useClientSettings } from "../lib/ClientSettingsContext";
import { plausibility, sequenceViolations, cellPlausibility } from "../lib/plausibility";
import { stepUuid, stepUuidShort } from "../lib/stepUuid";

type JobStatus = Awaited<ReturnType<typeof api.admin.testOcrStatus>>;
type ModelInfo = Awaited<ReturnType<typeof api.admin.testOcrModels>>["models"][number];
type ReadsState = Awaited<ReturnType<typeof api.admin.testOcrReads>>;

/**
 * Stundenliste OCR Test — Admin
 *
 * Stage A (foundation): PDF upload → grid-detect → annotated PNG
 * Stage B.1 (this iteration): click-to-OCR per cell with V4 (binary gate + CRNN)
 *
 * This UI is the **strategy documentation**. Every backend code path used
 * here is the same code path used by the future automated pipeline. The
 * test interface is not a separate tool — it IS the implementation.
 */
export function SettingsTestOcr(props: { fromCard?: { id: string; kind: "timesheet" | "expense" } } = {}) {
  const [tab, setTab] = useState<"new" | "history">("new");
  const [file, setFile] = useState<File | null>(null);
  const [jobId, setJobId] = useState<string | null>(null);  // = test_id (UUID)
  const [job, setJob] = useState<JobStatus | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const pollAbortRef = useRef<boolean>(false);
  const autoStartedRef = useRef<string | null>(null);

  // Stage B state
  const { settings, loaded: settingsLoaded } = useClientSettings();
  const [models, setModels] = useState<ModelInfo[]>([]);
  const [selectedModels, setSelectedModels] = useState<string[]>([]);
  const userTouchedModelsRef = useRef(false);

  // Pre-tick the production-default model so user doesn't have to choose
  // every time. Only fires once on initial settings load — user choices
  // after that are preserved via userTouchedModelsRef.
  useEffect(() => {
    if (settingsLoaded && !userTouchedModelsRef.current && selectedModels.length === 0) {
      const dflt = settings.production_ocr_model || "v4";
      setSelectedModels([dflt]);
    }
  }, [settingsLoaded, settings.production_ocr_model]);

  // Wrap setSelectedModels so user-driven changes mark "touched"
  const onSelectModelsChange = (next: string[]) => {
    userTouchedModelsRef.current = true;
    setSelectedModels(next);
  };

  const [reads, setReads] = useState<ReadsState | null>(null);
  const [activeCell, setActiveCell] = useState<{ day: number; field: string } | null>(null);
  const [cellStatus, setCellStatus] = useState<string>("");
  const [batchProgress, setBatchProgress] = useState<{ done: number; total: number } | null>(null);

  // History
  const [history, setHistory] = useState<Awaited<ReturnType<typeof api.admin.testOcrListRuns>>["runs"]>([]);
  const [historyFilter, setHistoryFilter] = useState<string>("");

  useEffect(() => {
    return () => { pollAbortRef.current = true; };
  }, []);

  useEffect(() => {
    api.admin.testOcrModels().then(r => setModels(r.models)).catch(() => {});
  }, []);

  // From-card auto-start: if mounted with `fromCard` prop, skip the upload UI
  // and start a fresh test run that loads the PDF from the existing card.
  // Used by Werkstatt OCR-Inspector "Per-Cell Debug" mode (2026-05-20).
  // Re-runs whenever the card id changes; debounces via autoStartedRef so
  // a re-render doesn't fire two parallel runs for the same card.
  useEffect(() => {
    if (!props.fromCard) return;
    const key = `${props.fromCard.kind}:${props.fromCard.id}`;
    if (autoStartedRef.current === key) return;
    autoStartedRef.current = key;
    setErr(null);
    setSubmitting(true);
    setJob(null);
    setReads(null);
    pollAbortRef.current = true;
    void (async () => {
      await new Promise(r => setTimeout(r, 50));
      pollAbortRef.current = false;
      try {
        const resp = await api.admin.testOcrStartFromCard(
          props.fromCard!.id, props.fromCard!.kind,
        );
        setJobId(resp.test_id);
        void pollLoop(resp.test_id);
      } catch (e: any) {
        setErr(`From-card start failed: ${e.message}`);
      } finally {
        setSubmitting(false);
      }
    })();
  }, [props.fromCard?.id, props.fromCard?.kind]);

  // Refresh reads whenever job goes done
  useEffect(() => {
    if (job?.status === "done" && jobId) {
      api.admin.testOcrReads(jobId).then(setReads).catch(() => {});
    }
  }, [job?.status, jobId]);

  const handleSubmit = async () => {
    if (!file) { setErr("Bitte zuerst PDF auswählen"); return; }
    setErr(null);
    setSubmitting(true);
    setJob(null);
    setReads(null);
    pollAbortRef.current = true;
    await new Promise(r => setTimeout(r, 50));
    pollAbortRef.current = false;

    try {
      const resp = await api.admin.testOcrStart(file);
      setJobId(resp.test_id);
      void pollLoop(resp.test_id);
    } catch (e: any) {
      setErr(`Upload fehlgeschlagen: ${e.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  const loadHistory = async () => {
    try {
      const r = await api.admin.testOcrListRuns(50, 0,
        historyFilter && historyFilter.length === 64 ? historyFilter : undefined);
      setHistory(r.runs);
    } catch (e: any) {
      setErr(`History laden fehlgeschlagen: ${e.message}`);
    }
  };

  useEffect(() => {
    if (tab === "history") {
      loadHistory();
    }
  }, [tab]);

  const loadRun = async (testId: string) => {
    setErr(null);
    setReads(null);
    setJob(null);
    try {
      const row = await api.admin.testOcrLoadRun(testId);
      setJobId(testId);
      // Synthesize JobStatus shape for the JobProgress component
      setJob({
        job_id: testId,
        status: row.pipeline_status,
        percent: row.pipeline_status === "done" ? 100 : 0,
        stage: row.pipeline_status,
        message: "Restored from history",
        stages_log: [],
        result: row.result,
        error: row.pipeline_error,
        error_detail: row.pipeline_error_detail,
        pdf_filename: row.pdf_filename,
      } as any);
      // Load reads
      if (row.pipeline_status === "done") {
        const r = await api.admin.testOcrReads(testId);
        setReads(r);
      }
      setTab("new");
    } catch (e: any) {
      setErr(`Load failed: ${e.message}`);
    }
  };

  const pollLoop = async (id: string) => {
    while (!pollAbortRef.current) {
      try {
        const status = await api.admin.testOcrStatus(id);
        setJob(status);
        if (status.status === "done" || status.status === "failed") break;
      } catch (e: any) {
        setErr(`Polling Fehler: ${e.message}`);
        break;
      }
      await new Promise(r => setTimeout(r, 600));
    }
  };

  const onCellClick = (day: number, field: string) => {
    // Klick aktiviert NUR die Zelle (zeigt Details). Kein erneutes OCR.
    setActiveCell({ day, field });
  };

  const runActiveCell = async (force = false) => {
    if (!jobId || !reads || !activeCell || selectedModels.length === 0) return;
    const { day, field } = activeCell;
    const cellKey = `d${String(day).padStart(2, "0")}_${field}`;
    // Nur Modelle ausführen, die für diese Zelle NOCH KEINE Reads haben
    // (außer force=true, dann alle ausgewählten erneut)
    const existingReads = reads.reads[cellKey] || {};
    const toRun = force ? selectedModels : selectedModels.filter(m => !existingReads[m]);
    if (toRun.length === 0) {
      setCellStatus(`d${day} ${field}: bereits gelesen — "Erneut lesen" für Re-Run`);
      return;
    }
    setCellStatus(`d${day} ${field}: ${toRun.join(", ")} verarbeitet…`);
    try {
      await api.admin.testOcrCell(jobId, day, field as any, toRun);
      const r = await api.admin.testOcrReads(jobId);
      setReads(r);
      setCellStatus(`d${day} ${field}: fertig`);
    } catch (e: any) {
      setCellStatus(`d${day} ${field}: Fehler — ${e.message}`);
    }
  };

  const resetActiveCell = async () => {
    if (!jobId || !activeCell) return;
    const { day, field } = activeCell;
    setCellStatus(`d${day} ${field}: zurücksetzen…`);
    try {
      await api.admin.testOcrClearCell(jobId, day, field);
      const r = await api.admin.testOcrReads(jobId);
      setReads(r);
      setCellStatus(`d${day} ${field}: zurückgesetzt`);
    } catch (e: any) {
      setCellStatus(`d${day} ${field}: Fehler — ${e.message}`);
    }
  };

  // GT-Source: wenn aus existierender Karte geladen, wird der card_id für
  // den finalize-payload erhalten — so wissen wir bei Werkstatt-Visibility
  // welche Karte als GT verwendet wurde.
  const [gtSourceCardId, setGtSourceCardId] = useState<string | null>(null);

  const runAllCells = async () => {
    if (!jobId || !reads || selectedModels.length === 0) return;
    const cellKeys = Object.values(reads.cell_bboxes).map(b => ({ day: b.day, field: b.field }));
    setBatchProgress({ done: 0, total: cellKeys.length });
    const startMs = Date.now();
    for (let i = 0; i < cellKeys.length; i++) {
      const entry = cellKeys[i];
      if (!entry) continue;
      const { day, field } = entry;
      setCellStatus(`Batch ${i + 1}/${cellKeys.length}: d${day} ${field}`);
      try {
        await api.admin.testOcrCell(jobId, day, field as any, selectedModels);
      } catch (e) {
        /* continue on errors */
      }
      setBatchProgress({ done: i + 1, total: cellKeys.length });
      // periodic refresh every 10 cells
      if ((i + 1) % 10 === 0 || i === cellKeys.length - 1) {
        const r = await api.admin.testOcrReads(jobId);
        setReads(r);
      }
    }
    const durationS = (Date.now() - startMs) / 1000;
    setCellStatus(`Batch fertig (${durationS.toFixed(1)}s)`);
    setBatchProgress(null);

    // Finalize: duration + Metadaten persistieren damit Werkstatt sehen kann
    // wie der Test-Run lief. (Audit-Trail für Iterations-Vergleich.)
    try {
      // Totals berechnen aus aktuellen reads
      const finalReads = await api.admin.testOcrReads(jobId);
      setReads(finalReads);
      const cellTotal = Object.keys(finalReads.cell_bboxes).length;
      let cellsWithRead = 0, cellsWithValue = 0;
      for (const ck of Object.keys(finalReads.cell_bboxes)) {
        const r = finalReads.reads[ck];
        if (r && Object.keys(r).length > 0) cellsWithRead++;
        for (const m of selectedModels) {
          const v = r?.[m]?.value;
          if (v !== null && v !== undefined && v !== "") { cellsWithValue++; break; }
        }
      }
      await api.admin.testOcrFinalize(jobId, {
        duration_s: durationS,
        models_used: selectedModels,
        totals: {
          cells_total: cellTotal,
          cells_with_read: cellsWithRead,
          cells_with_value: cellsWithValue,
        },
        from_card_id: props.fromCard?.id,
        gt_source_card_id: gtSourceCardId ?? undefined,
      });
    } catch (e: any) {
      console.warn("finalize failed (non-fatal):", e.message);
    }
  };

  const handleGtUpload = async (file: File) => {
    if (!jobId) return;
    try {
      const result = await api.admin.testOcrUploadGt(jobId, file);
      setCellStatus(`GT geladen: ${result.gt_count} cells`);
      setGtSourceCardId(null);  // CSV ist nicht von einer Karte
      const r = await api.admin.testOcrReads(jobId);
      setReads(r);
    } catch (e: any) {
      setErr(`GT upload failed: ${e.message}`);
    }
  };

  const handleGtFromCard = async (sourceCardId: string, sourceKind: "timesheet" | "expense") => {
    if (!jobId) return;
    try {
      const result = await api.admin.testOcrGtFromCard(jobId, sourceCardId, sourceKind);
      setCellStatus(`GT von Karte ${sourceCardId.slice(0, 8)} geladen: ${result.gt_count} cells`);
      setGtSourceCardId(sourceCardId);
      const r = await api.admin.testOcrReads(jobId);
      setReads(r);
    } catch (e: any) {
      setErr(`GT von Karte fehlgeschlagen: ${e.message}`);
    }
  };

  const fromCard = props.fromCard;
  return (
    <div style={{ maxWidth: 1400 }}>
      {!fromCard && <Header />}
      {!fromCard && <TabBar tab={tab} setTab={setTab} />}
      {fromCard && (
        <div style={{ padding: "8px 12px", background: "#eef6ff", border: "1px solid #c7e0ff", borderRadius: 6, marginBottom: 12, fontSize: 13 }}>
          🔬 <strong>Per-Cell Debug</strong> für Karte <code style={{ fontFamily: "monospace" }}>{fromCard.id.slice(0, 8)}…</code> ({fromCard.kind}). PDF wurde aus dem Pipeline-Storage geladen.
        </div>
      )}
      {tab === "new" && (
        <>
          {jobId && !fromCard && <TestIdBanner testId={jobId} pdfFilename={(job as any)?.pdf_filename ?? file?.name} />}
          {!fromCard && (
            <UploadRow file={file} setFile={setFile} submitting={submitting}
                       handleSubmit={handleSubmit} jobRunning={job?.status === "running" || job?.status === "queued"} />
          )}
          {err && <Alert>{err}</Alert>}
          {job && <JobProgress job={job} reads={reads} cellBboxes={reads?.cell_bboxes ?? null}
                               cardIdForUuids={fromCard?.id} />}
          {job?.status === "done" && jobId && reads && (
            <StageBPanel
              jobId={jobId} reads={reads} models={models}
              selectedModels={selectedModels} setSelectedModels={onSelectModelsChange}
              onCellClick={onCellClick} runAllCells={runAllCells}
              cellStatus={cellStatus} batchProgress={batchProgress}
              activeCell={activeCell}
              onGtUpload={handleGtUpload}
              onGtFromCard={handleGtFromCard}
              gtSourceCardId={gtSourceCardId}
              runActiveCell={runActiveCell}
              resetActiveCell={resetActiveCell}
              cardIdForUuids={fromCard?.id}
            />
          )}
          {job?.status === "failed" && jobId && <FailedView job={job} jobId={jobId} />}
        </>
      )}
      {tab === "history" && (
        <HistoryView
          runs={history}
          filter={historyFilter}
          setFilter={setHistoryFilter}
          onRefresh={loadHistory}
          onLoadRun={loadRun}
        />
      )}
    </div>
  );
}


function TabBar({ tab, setTab }: { tab: "new" | "history"; setTab: (t: "new" | "history") => void }) {
  return (
    <div style={{ display: "flex", gap: 4, marginBottom: 12, borderBottom: "1px solid #ddd" }}>
      {(["new", "history"] as const).map(t => (
        <button key={t}
          onClick={() => setTab(t)}
          style={{
            padding: "8px 16px",
            background: tab === t ? "#fff" : "#f4f4f4",
            border: "1px solid #ddd",
            borderBottom: tab === t ? "1px solid #fff" : "1px solid #ddd",
            marginBottom: -1,
            cursor: "pointer",
            fontWeight: tab === t ? "bold" : "normal",
          }}>
          {t === "new" ? "Neuer Test" : "Frühere Tests"}
        </button>
      ))}
    </div>
  );
}


function TestIdBanner({ testId, pdfFilename }: { testId: string; pdfFilename?: string }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(testId).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12,
                  padding: "10px 14px", background: "#f0f4ff",
                  border: "1px solid #c4d4ff", borderRadius: 6, marginBottom: 12 }}>
      <span style={{ fontSize: 12, color: "#555", fontWeight: "bold" }}>Test ID:</span>
      <code style={{ fontFamily: "monospace", fontSize: 13, color: "#222" }}>{testId}</code>
      <button onClick={copy} style={{
        padding: "4px 10px", fontSize: 12, cursor: "pointer",
        background: copied ? "#9c9" : "#fff",
        border: "1px solid #ccc", borderRadius: 4,
      }}>
        {copied ? "kopiert ✓" : "kopieren"}
      </button>
      {pdfFilename && (
        <span style={{ fontSize: 12, color: "#666", marginLeft: "auto" }}>
          {pdfFilename}
        </span>
      )}
    </div>
  );
}


function HistoryView(props: {
  runs: Awaited<ReturnType<typeof api.admin.testOcrListRuns>>["runs"];
  filter: string; setFilter: (s: string) => void;
  onRefresh: () => void;
  onLoadRun: (id: string) => void;
}) {
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [confirm, setConfirm] = useState<null | { kind: "selected" | "all" | "single"; ids?: string[] }>(null);
  const [busy, setBusy] = useState(false);

  const visible = props.filter
    ? props.runs.filter(r =>
        r.test_id.includes(props.filter) ||
        r.pdf_filename.toLowerCase().includes(props.filter.toLowerCase()))
    : props.runs;

  const allVisibleSelected = visible.length > 0 && visible.every(r => selected.has(r.test_id));
  const someSelected = visible.some(r => selected.has(r.test_id));

  const toggleOne = (id: string) => {
    const s = new Set(selected);
    s.has(id) ? s.delete(id) : s.add(id);
    setSelected(s);
  };
  const toggleAll = () => {
    if (allVisibleSelected) {
      const s = new Set(selected);
      visible.forEach(r => s.delete(r.test_id));
      setSelected(s);
    } else {
      const s = new Set(selected);
      visible.forEach(r => s.add(r.test_id));
      setSelected(s);
    }
  };

  const doDelete = async () => {
    if (!confirm) return;
    setBusy(true);
    try {
      if (confirm.kind === "all") {
        await api.admin.testOcrDeleteBatch({ all: true });
      } else if (confirm.kind === "single" && confirm.ids && confirm.ids[0]) {
        await api.admin.testOcrDeleteRun(confirm.ids[0]);
      } else if (confirm.kind === "selected" && confirm.ids) {
        await api.admin.testOcrDeleteBatch(confirm.ids);
      }
      setSelected(new Set());
      setConfirm(null);
      props.onRefresh();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 10 }}>
        <input type="text" placeholder="Filter (test_id, filename, oder PDF-sha256)"
               value={props.filter}
               onChange={e => props.setFilter(e.target.value)}
               style={{ flex: 1, padding: "6px 10px", fontSize: 13,
                        border: "1px solid #ccc", borderRadius: 4 }} />
        <button onClick={props.onRefresh} style={{ padding: "6px 14px", fontSize: 13 }}>↻ Refresh</button>
        <button onClick={() => setConfirm({ kind: "all" })}
                disabled={props.runs.length === 0}
                title="Alle Tests aus der Datenbank löschen"
                style={{ padding: "6px 14px", fontSize: 13, color: "#a00",
                         background: "#fff", border: "1px solid #faa", borderRadius: 4,
                         opacity: props.runs.length === 0 ? 0.5 : 1 }}>
          ⌧ Alle löschen
        </button>
        <span style={{ fontSize: 12, color: "#888" }}>{visible.length} runs</span>
      </div>
      <div style={{ maxHeight: 600, overflow: "auto", border: "1px solid #ddd" }}>
        <table style={{ width: "100%", fontSize: 12, borderCollapse: "collapse" }}>
          <thead style={{ position: "sticky", top: 0, background: "#fafafa", zIndex: 1 }}>
            <tr>
              <th style={{ ...th, width: 28, textAlign: "center" }}>
                <input type="checkbox" checked={allVisibleSelected}
                       ref={el => { if (el) el.indeterminate = !allVisibleSelected && someSelected; }}
                       onChange={toggleAll}
                       title={allVisibleSelected ? "Auswahl aufheben" : "Alle sichtbaren auswählen"} />
              </th>
              <th style={th}>Test ID</th>
              <th style={th}>PDF</th>
              <th style={th}>Status</th>
              <th style={th}>Form</th>
              <th style={th}>Strategy</th>
              <th style={th}>Rows</th>
              <th style={th}>Reads</th>
              <th style={th}>Created</th>
              <th style={{ ...th, width: 30 }}></th>
            </tr>
          </thead>
          <tbody>
            {visible.map(r => {
              const idShort = r.test_id.slice(0, 8);
              const color = r.pipeline_status === "done" ? "#0a7"
                          : r.pipeline_status === "failed" ? "#a00" : "#a70";
              const isSelected = selected.has(r.test_id);
              return (
                <tr key={r.test_id}
                    style={{ borderBottom: "1px solid #f0f0f0",
                             background: isSelected ? "#fff4e6" : "transparent" }}>
                  <td style={{ ...td, textAlign: "center" }}
                      onClick={e => e.stopPropagation()}>
                    <input type="checkbox" checked={isSelected}
                           onChange={() => toggleOne(r.test_id)} />
                  </td>
                  <td style={{ ...td, fontFamily: "monospace", cursor: "pointer" }}
                      title={r.test_id}
                      onClick={() => props.onLoadRun(r.test_id)}>
                    {idShort}…
                  </td>
                  <td style={{ ...td, cursor: "pointer" }}
                      onClick={() => props.onLoadRun(r.test_id)}>{r.pdf_filename}</td>
                  <td style={{ ...td, color, fontWeight: "bold", cursor: "pointer" }}
                      onClick={() => props.onLoadRun(r.test_id)}>
                    {r.pipeline_status}
                    {r.pipeline_error && <span style={{ fontSize: 10, color: "#888", marginLeft: 4 }}>({r.pipeline_error})</span>}
                  </td>
                  <td style={{ ...td, cursor: "pointer" }} onClick={() => props.onLoadRun(r.test_id)}>{r.form_type ?? "—"}</td>
                  <td style={{ ...td, fontFamily: "monospace", cursor: "pointer" }}
                      onClick={() => props.onLoadRun(r.test_id)}>{r.strategy_used ?? "—"}</td>
                  <td style={{ ...td, cursor: "pointer" }} onClick={() => props.onLoadRun(r.test_id)}>{r.rows_count ?? 0}/31</td>
                  <td style={{ ...td, cursor: "pointer" }} onClick={() => props.onLoadRun(r.test_id)}>{r.n_cells_with_reads}/{r.rows_count ? r.rows_count * 4 : 124}</td>
                  <td style={{ ...td, cursor: "pointer" }} onClick={() => props.onLoadRun(r.test_id)}>{r.created_at?.slice(5, 16).replace("T", " ")}</td>
                  <td style={td} onClick={e => e.stopPropagation()}>
                    <button onClick={() => setConfirm({ kind: "single", ids: [r.test_id] })}
                            title="Diesen Test löschen"
                            style={{ background: "transparent", border: "none",
                                     color: "#a00", cursor: "pointer", padding: 4,
                                     fontSize: 14, opacity: 0.5 }}
                            onMouseEnter={e => (e.currentTarget.style.opacity = "1")}
                            onMouseLeave={e => (e.currentTarget.style.opacity = "0.5")}>
                      ✕
                    </button>
                  </td>
                </tr>
              );
            })}
            {visible.length === 0 && (
              <tr><td colSpan={10} style={{ padding: 20, color: "#888", textAlign: "center" }}>
                Keine Tests gefunden
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Floating action bar when items selected */}
      {selected.size > 0 && (
        <div style={{
          position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)",
          padding: "10px 16px", background: "#222", color: "white",
          borderRadius: 8, boxShadow: "0 4px 14px rgba(0,0,0,0.3)",
          display: "flex", alignItems: "center", gap: 12, zIndex: 100,
        }}>
          <span style={{ fontSize: 13 }}>
            <strong>{selected.size}</strong> Test{selected.size === 1 ? "" : "s"} ausgewählt
          </span>
          <button onClick={() => setConfirm({ kind: "selected", ids: Array.from(selected) })}
                  style={{ padding: "6px 14px", background: "#c44",
                           color: "white", border: "none", borderRadius: 4,
                           cursor: "pointer", fontSize: 13 }}>
            ⌧ Auswahl löschen
          </button>
          <button onClick={() => setSelected(new Set())}
                  style={{ padding: "6px 12px", background: "transparent",
                           color: "white", border: "1px solid #666",
                           borderRadius: 4, cursor: "pointer", fontSize: 13 }}>
            Aufheben
          </button>
        </div>
      )}

      {/* Confirmation modal */}
      {confirm && (
        <div style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)",
          display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000,
        }}
        onClick={() => !busy && setConfirm(null)}>
          <div onClick={e => e.stopPropagation()}
               style={{
                 background: "white", padding: 24, borderRadius: 8,
                 maxWidth: 420, boxShadow: "0 10px 30px rgba(0,0,0,0.3)",
               }}>
            <h3 style={{ margin: "0 0 12px", color: "#a00" }}>
              {confirm.kind === "all" ? "ALLE Tests löschen?" :
               confirm.kind === "selected" ? `${confirm.ids?.length} Test${confirm.ids?.length === 1 ? "" : "s"} löschen?` :
               "Diesen Test löschen?"}
            </h3>
            <p style={{ fontSize: 13, color: "#555", lineHeight: 1.5, marginTop: 0 }}>
              {confirm.kind === "all"
                ? "Alle Test-Runs in der Datenbank werden inkl. PDFs und annotierten Bildern gelöscht. Diese Aktion kann nicht rückgängig gemacht werden."
                : "Die ausgewählten Test-Runs werden inkl. PDFs und annotierten Bildern gelöscht. Diese Aktion kann nicht rückgängig gemacht werden."}
            </p>
            {confirm.ids && confirm.ids.length <= 5 && (
              <ul style={{ fontSize: 11, fontFamily: "monospace", color: "#888",
                           background: "#f8f8f8", padding: "8px 12px 8px 24px",
                           borderRadius: 4, maxHeight: 100, overflow: "auto" }}>
                {confirm.ids.map(id => <li key={id}>{id}</li>)}
              </ul>
            )}
            <div style={{ display: "flex", gap: 8, marginTop: 16, justifyContent: "flex-end" }}>
              <button onClick={() => setConfirm(null)} disabled={busy}
                      style={{ padding: "8px 16px", fontSize: 13,
                               background: "#f4f4f4", border: "1px solid #ccc",
                               borderRadius: 4, cursor: "pointer" }}>
                Abbrechen
              </button>
              <button onClick={doDelete} disabled={busy}
                      style={{ padding: "8px 16px", fontSize: 13,
                               background: "#c44", color: "white", border: "none",
                               borderRadius: 4, cursor: "pointer", fontWeight: "bold" }}>
                {busy ? "lösche…" : "Löschen"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const th: React.CSSProperties = {
  textAlign: "left", padding: "6px 8px", borderBottom: "1px solid #ccc",
  fontWeight: "bold", color: "#333",
};
const td: React.CSSProperties = {
  padding: "5px 8px",
};


function Header() {
  return (
    <div style={{ marginBottom: 18 }}>
      <h2 style={{ margin: "0 0 8px" }}>Stundenliste OCR-Test — Strategie-Werkstatt</h2>
      <p style={{ color: "#555", fontSize: 13, lineHeight: 1.5, margin: 0 }}>
        Diese Seite ist die <strong>Live-Dokumentation der OCR-Strategie</strong>. Jeder Klick
        ruft den selben Code auf, den die spätere automatische Pipeline verwendet —
        was hier funktioniert, läuft 1:1 in der Massenverarbeitung.
      </p>
      <details style={{ marginTop: 10, fontSize: 13 }}>
        <summary style={{ cursor: "pointer", color: "#333" }}>Wie funktioniert das?</summary>
        <ol style={{ marginTop: 8, paddingLeft: 20, color: "#555", lineHeight: 1.6 }}>
          <li><strong>Stufe A — Grid-Erkennung</strong>: PDF wird gerendert, Form-Typ klassifiziert,
              31 Zeilen × 4 Spalten-Bboxes lokalisiert. Strategien werden in Kette versucht
              (Template → Morphologie → Anker-begrenzt → OCR-Anker → CLAHE-Rettung).</li>
          <li><strong>Stufe B — Zell-OCR per Klick</strong>: Zellen sind klickbar. Modell(e) auswählen,
              im Detail-Panel „Lesen" drücken → einzelne Zelle wird ans Modell gesendet, Wert + Konfidenz
              kommen zurück.</li>
          <li><strong>Stufe C — Stapel + Vergleich</strong>: „Alle Zellen verarbeiten" startet OCR auf
              allen Zellen mit allen ausgewählten Modellen. GT-CSV optional → Live-Score-Vergleich.</li>
        </ol>
      </details>
    </div>
  );
}


function UploadRow(props: {
  file: File | null; setFile: (f: File | null) => void;
  submitting: boolean; handleSubmit: () => void; jobRunning: boolean;
}) {
  return (
    <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 16,
                  padding: 12, background: "#f7f7fb", borderRadius: 6 }}>
      <input type="file" accept="application/pdf,.pdf"
             onChange={e => props.setFile(e.target.files?.[0] ?? null)} />
      <button onClick={props.handleSubmit}
              disabled={!props.file || props.submitting || props.jobRunning}
              style={{ padding: "8px 16px" }}>
        {props.jobRunning ? "läuft…" : "Pipeline starten"}
      </button>
      {props.file && (
        <span style={{ color: "#888", fontSize: 13 }}>
          {props.file.name} ({(props.file.size / 1024).toFixed(1)} KB)
        </span>
      )}
    </div>
  );
}


function Alert(props: { children: any }) {
  return (
    <div style={{ background: "#fee", border: "1px solid #fcc", padding: 10,
                  marginBottom: 12, borderRadius: 4 }}>{props.children}</div>
  );
}


// ─── Pipeline Step Indicator ────────────────────────────────────────────────
//
// The OCR-Test UI IS the strategy documentation. Each step the backend
// performs is shown as a numbered circle with title + description + state,
// not buried in a stages_log table. Empty bubbles = pending, blue = active,
// green = done with summary, red = error.

// Konsistente Status-Sprache (eine Legende für Tabs, Räume, Schritte):
//   pending  = noch nicht dran (Twin nie angekommen)
//   active   = läuft gerade (Maschine arbeitet)
//   waiting  = wartet auf dich (Eingang erfüllt, manuelle Aktion nötig — KEIN Fehler)
//   done     = fertig / abgeschlossen
//   error    = Fehler — hängt hier (echtes Problem, NICHT „Ende")
//   blocked  = blockiert durch gestoppten Vorgänger
type StepStatus = "pending" | "active" | "waiting" | "done" | "error" | "blocked";

export interface PipelineStep {
  num: number;
  title: string;
  description: string;
  status: StepStatus;
  /** Detail-Text oder JSX. Bei einem Verweis auf eine andere Phase soll der
   * Hinweis ein Link sein, damit der User geführt navigiert (Glass-Box-Prinzip:
   * "verwiesene Aktion ist klickbar"). String wird als reiner Text gerendert. */
  detail?: React.ReactNode;
  duration_ms?: number;
  optional?: boolean;  // recovery passes only fire conditionally
  /** Deterministic step-UUID — same input always produces the same id.
   * Set by Werkstatt phases via stepUuid(cardId, phase, num). When present,
   * PipelineStepList renders a click-to-copy chip so the user can address
   * a specific step in conversation. */
  uuid?: string;
  /** Rekursive Sub-Schritte = der „nächste Raum" (Glass-Box-Zwiebel: Zoom in
   * die Evidenz/Unter-Schritte). PipelineStepList rendert children eingerückt
   * als Unter-/Unter-unter-Liste. Optional — flache Steps bleiben unverändert. */
  children?: PipelineStep[];
  /** Begründung für status="error"/"blocked" — wird im Stuck-Hinweis angezeigt. */
  blockedReason?: string;
}

/**
 * Sequenzielle Integrität (Glass-Box): ab dem ersten error-Schritt sind alle
 * FOLGE-Geschwister blocked (die Pipeline steckt — kein „läuft weiter"). Wirkt
 * rekursiv auf jede children-Ebene. Idempotent; bereits gesetzte error/blocked
 * bleiben erhalten. Reihenfolge = Array-Reihenfolge (= Sequenz).
 */
export function propagateStuck(steps: PipelineStep[]): PipelineStep[] {
  let tripped: PipelineStep | null = null;
  return steps.map((s) => {
    const child = s.children ? propagateStuck(s.children) : undefined;
    if (tripped) {
      return { ...s, status: "blocked" as StepStatus,
        blockedReason: s.blockedReason ?? `Vorgänger gestoppt: „${tripped.title}"`,
        children: child };
    }
    if (s.status === "error") tripped = s;
    return child ? { ...s, children: child } : s;
  });
}

/**
 * Map the backend stages_log (flat events) into structured Step records.
 * The mapping makes the strategy auditable in the UI: each backend stage
 * marker corresponds to a numbered step. See ocr/pipeline_core.py for the
 * backend side of this contract.
 */
function deriveSteps(job: JobStatus | null, reads: ReadsState | null,
                    cellBboxes: Record<string, any> | null,
                    cardIdForUuids?: string): PipelineStep[] {
  // Glass-Box principle: every step gets a stable UUID derived from
  // (cardId, "test-pipeline", stepNum). User can reference each step in
  // conversation ("Stufe 6 — Per-Cell OCR (uuid abc123…)"), the chip is
  // click-to-copy. UUIDs only generated when we're embedded with a real
  // card; standalone test-pipeline runs (PDF upload) don't have a card.
  const u = (n: number | string): string | undefined =>
    cardIdForUuids ? stepUuid(cardIdForUuids, "test-pipeline", n) : undefined;
  const log = job?.stages_log ?? [];
  const has = (stage: string) => log.find((s) => s.stage === stage);
  const tsOf = (stage: string) => has(stage)?.ts;
  const dur = (start: string, end: string): number | undefined => {
    const a = tsOf(start), b = tsOf(end);
    return (a && b) ? Math.round((b - a) * 1000) : undefined;
  };

  const failed = job?.status === "failed";
  const pipelineDone = job?.status === "done";
  const ranAnyRecovery = !!(has("deskew_recovery") || has("clahe_recovery") || has("aggressive_recovery"));
  // Loaded from history: stages_log is empty but pipeline IS done. In that
  // case the pipeline stages 1-4 are all complete by definition.
  const restoredFromHistory = pipelineDone && log.length === 0;

  // Step 1: Render PDF
  const s1: PipelineStep = {
    num: 1, title: "PDF rendern (220 DPI)",
    description: "Erste Seite via PyMuPDF auf 220 DPI BGR konvertieren (pipeline_core.PIPELINE_DPI).",
    status: has("render_done") || restoredFromHistory ? "done"
          : has("rendering_pdf") ? "active" : (failed ? "error" : "pending"),
    detail: has("render_done")?.message ?? (restoredFromHistory ? "wiederhergestellt aus History" : undefined),
    duration_ms: dur("rendering_pdf", "render_done"),
    uuid: u(1),
  };

  // Step 2: Form-type detection — mit rekursiver Evidenz-Unterliste (der „nächste Raum").
  const fi: any = (job as any)?.form_info ?? (job as any)?.error_detail?.form_info ?? null;
  const s2Children: PipelineStep[] | undefined = fi ? [
    ...(["0", "90", "180", "270"] as const).map((deg, i) => {
      const preview = fi.rotations?.[deg] ?? (deg === "0" ? fi.preview_0deg : "");
      const usable = !!preview && preview.replace(/[^a-zA-ZäöüÄÖÜ]/g, "").length >= 6;
      return {
        num: i + 1,
        title: `Rotation ${deg}° — Top-Region OCR`,
        description: usable ? "lesbarer Text" : "Rauschen (Seite nicht in dieser Lage)",
        status: (usable ? "done" : "pending") as StepStatus,
        detail: preview ? <code style={{ fontSize: 11 }}>{preview}</code> : "(leer)",
        uuid: u(`2.rot.${deg}`),
      } as PipelineStep;
    }),
    {
      num: 5,
      title: "Keyword-Match + Entscheidung",
      description: "Hits: Stundenliste vs Diäten → Form-Typ-Regel.",
      status: "done" as StepStatus,
      detail: (
        <span>
          Stundenliste-Hits: <strong>{fi.stundenliste_hits ?? 0}</strong> {fi.s_keys?.length ? `[${fi.s_keys.join(", ")}]` : "[]"}
          {" · "}Diäten-Hits: <strong>{fi.diaeten_hits ?? 0}</strong> {fi.d_keys?.length ? `[${fi.d_keys.join(", ")}]` : "[]"}
          {fi.decision && <> {" → "}<strong>{fi.decision.form_type}</strong> ({fi.decision.rule})</>}
        </span>
      ),
      uuid: u("2.decision"),
    },
  ] : undefined;
  const s2: PipelineStep = {
    num: 2, title: "Form-Typ erkennen",
    description: "Tesseract auf Top-Region (4 Rotationen), Keyword-Match Stundenliste vs Diäten.",
    status: has("form_type_done") || restoredFromHistory ? "done"
          : has("form_type_detect") ? "active" : "pending",
    detail: has("form_type_done")?.message ?? (restoredFromHistory ? "wiederhergestellt aus History" : undefined),
    duration_ms: dur("form_type_detect", "form_type_done"),
    uuid: u(2),
    children: s2Children,
  };

  // Step 3: Grid detection
  const recoveryNotes: string[] = [];
  if (has("deskew_recovered")) recoveryNotes.push("deskew");
  if (has("clahe_recovered")) recoveryNotes.push("CLAHE");
  if (has("aggressive_recovered")) recoveryNotes.push("aggressive CLAHE");
  const s3: PipelineStep = {
    num: 3, title: "Grid-Detection",
    description: "Strategy-Chain: primary → deskew → CLAHE → aggressive CLAHE. Erste erfolgreiche Pass gewinnt.",
    status: has("grid_detect_done") || restoredFromHistory ? "done"
          : has("grid_detect_failed") ? "error"
          : (has("grid_detect_start") || ranAnyRecovery) ? "active"
          : "pending",
    detail: has("grid_detect_done")?.message
          ? has("grid_detect_done")?.message + (recoveryNotes.length ? ` · Recovery: ${recoveryNotes.join(" + ")}` : "")
          : (restoredFromHistory ? "wiederhergestellt aus History" : undefined),
    duration_ms: dur("grid_detect_start", "grid_detect_done"),
    uuid: u(3),
  };

  // Step 4: Cell bboxes computed (uses annotate_done as proxy — bboxes are
  // built right before annotation)
  const nCells = cellBboxes ? Object.keys(cellBboxes).length : 0;
  const s4: PipelineStep = {
    num: 4, title: "Zell-Bboxes extrahieren",
    description: "Aus 31 Zeilen × 5 Spalten die per-cell bboxes berechnen (124 Time + 31 Symb).",
    status: pipelineDone ? "done" : has("annotate_start") ? "active" : "pending",
    detail: pipelineDone ? `${nCells} Zellen, gespeichert für Compare-View` : undefined,
    duration_ms: pipelineDone ? dur("annotate_start", "annotate_done") : undefined,
    uuid: u(4),
  };

  // Step 5: Header-OCR (Mitarbeiter + Monat). Production-only step — runs
  // inside cell_dispatch.process_timesheet_from_pdf via qwen3_header_text.
  // /admin/test-pipeline does NOT run this — it's a per-cell test harness.
  const s5: PipelineStep = {
    num: 5, title: "Header-OCR (Mitarbeiter + Monat)",
    description:
      "Region oberhalb der ersten Datenzeile crop'pen + qwen3 mit strukturiertem Prompt "
      + "'name=<NAME>;month=<MONTH>'. Ergebnis fließt in TimesheetResult.header.employee_raw, "
      + "Worker führt anschließend Levenshtein-Match gegen die employees-Tabelle und "
      + "schreibt Vorauswahl in data.header.suggestions. UI zeigt die Vorschläge als "
      + "Buttons unter dem Mitarbeiter-Dropdown. "
      + "Production-only: läuft im cell_dispatch.process_timesheet_from_pdf, nicht im OCR-Test-Harness.",
    status: "pending",
    detail: "Im OCR-Test nicht ausgeführt — sichtbar im Production-Pfad auf /timesheets/<id>.",
    optional: true,
    uuid: u(5),
  };

  // Step 6: Per-cell OCR (client-driven — user picks model + clicks/batches)
  const readsCount = reads?.reads ? Object.keys(reads.reads).length : 0;
  const totalCells = nCells || 124;
  const s6: PipelineStep = {
    num: 6, title: "Per-Cell OCR",
    description:
      "Modell auswählen (siehe Picker) und Zellen einzeln klicken ODER per Batch alle laufen lassen. " +
      "Production-Default: qwen3 Strategy K3 für Zeit-Spalten (force-time-prompt + conf-filter ≥ 0.85). " +
      "Symbol-Spalte: separater qwen3-Handler 'qwen3_symb' mit Closed-List-Prompt {K, U, ZA, F, P, DV, KU, LA, FASCHING, SONNTAG, FREI} — ersetzt den 2025-trainierten ONNX-Classifier, der nach dem 100→220 DPI-Wechsel unbrauchbar wurde.",
    status: !pipelineDone ? "pending"
          : readsCount === 0 ? "pending"
          : readsCount < totalCells ? "active"
          : "done",
    detail: pipelineDone ? `${readsCount} / ${totalCells} Zellen gelesen` : undefined,
    uuid: u(6),
  };

  // Step 6: Plausibility check (client-side, derived from reads).
  //   Rule A (value): Minuten 00/15/30/45 = ok, 05/10/20/... = gelb, sonst rot
  //   Rule B (sequence): von1 < bis1 < von2 < bis2 — Verstoß immer rot.
  //   Rule C (symbol-exclusivity): Symbol gesetzt → keine Zeitwerte erwartet.
  // For multi-cell checks we group reads by day+model — reads is
  // keyed cell_key→{model: result}, so we synthesize day-level inputs per model.
  let nRed = 0, nYellow = 0, nOk = 0, nSeqViolations = 0, nSymbExclusivity = 0;
  if (reads?.reads) {
    // Group by day+model for multi-cell checks
    const byDay: Record<number, Record<string, Record<string, string>>> = {};
    for (const [cellKey, cell] of Object.entries(reads.reads) as [string, any][]) {
      const m = cellKey.match(/^d(\d{2})_(von1|bis1|von2|bis2|symb)$/);
      if (!m) continue;
      const day = Number(m[1]);
      const field = m[2];
      if (!field) continue;
      for (const [model, result] of Object.entries(cell) as [string, any][]) {
        if (!result?.value) continue;
        (byDay[day] ??= {})[model] ??= {};
        byDay[day]![model]![field] = result.value;
      }
    }
    // Sequence violations (Rule B)
    for (const dayMap of Object.values(byDay)) {
      for (const fields of Object.values(dayMap)) {
        const vios = sequenceViolations(fields as any);
        nSeqViolations += Object.keys(vios).length;
      }
    }
    // Aggregate per-cell verdict via cellPlausibility (handles A + B + C)
    for (const [cellKey, cell] of Object.entries(reads.reads) as [string, any][]) {
      const m = cellKey.match(/^d(\d{2})_(von1|bis1|von2|bis2|symb)$/);
      if (!m) continue;
      const day = Number(m[1]);
      const field = m[2] as any;
      for (const [model, result] of Object.entries(cell) as [string, any][]) {
        if (!result?.value && field !== "symb") continue;
        const fields = byDay[day]?.[model] ?? {};
        const cp = cellPlausibility(field, result?.value, fields as any);
        if (cp.severity === "red") nRed++;
        else if (cp.severity === "yellow") nYellow++;
        else if (result?.value) nOk++;
        // Detect Rule-C hit: "Symbol ... gesetzt — Zeit" or "Symbol ... gesetzt — aber Zeit"
        if (cp.reasons.some(r => r.startsWith("Symbol "))) nSymbExclusivity++;
      }
    }
  }
  const s7: PipelineStep = {
    num: 7, title: "Plausibilitätsprüfung",
    description:
      "Regel A (Wert): Viertelstunden (00/15/30/45) = OK · 5-Min-Raster (05/10/20/...) = gelb · sonst rot (z.B. 08:03 → vermutlich OCR-Fehler). " +
      "Regel B (Reihenfolge): von1 < bis1 < von2 < bis2 muss gelten — jede Sequenz-Verletzung ist rot. " +
      "Regel C (Symbol-Exklusivität): wenn Symbol-Spalte gesetzt ist (K, U, ZA, …), dürfen KEINE Zeitwerte für den Tag eingetragen sein — Symbol und Zeiten schließen sich aus.",
    status: readsCount === 0 ? "pending" : "done",
    detail: readsCount > 0
      ? `${nOk} ok · ${nYellow} gelb · ${nRed} rot · ${nSeqViolations} Sequenz-Verstöße · ${nSymbExclusivity} Symbol/Zeit-Konflikte`
      : undefined,
    uuid: u(7),
  };

  // Step 8: GT-Comparison (only if user uploaded a CSV)
  const hasGT = !!(reads?.gt && Object.keys(reads.gt).length > 0);
  const s8: PipelineStep = {
    num: 8, title: "GT-Vergleich (optional)",
    description: "Ground-Truth-CSV hochladen — pro Zelle TP/WV/FP/TN/FN/Silent + Aggregate-Matrix.",
    status: hasGT ? "done" : "pending",
    detail: hasGT ? `${Object.keys(reads!.gt).length} GT-Cells geladen` : undefined,
    optional: true,
    uuid: u(8),
  };

  return [s1, s2, s3, s4, s5, s6, s7, s8];
}

function StepCircle({ status, num }: { status: StepStatus; num: number }) {
  const styles: Record<StepStatus, React.CSSProperties> = {
    pending: { background: "#fff", color: "#999", border: "2px solid #ddd" },
    active:  { background: "#dbeafe", color: "#1d4ed8", border: "2px solid #3b82f6" },
    waiting: { background: "#fef3c7", color: "#b45309", border: "2px solid #f59e0b" },
    done:    { background: "#10b981", color: "#fff", border: "2px solid #10b981" },
    error:   { background: "#dc2626", color: "#fff", border: "2px solid #dc2626" },
    blocked: { background: "#f3f4f6", color: "#9ca3af", border: "2px dashed #9ca3af" },
  };
  const glyph: Record<StepStatus, string | number> = {
    pending: num, active: "▶", waiting: "✋", done: "✓", error: "✗", blocked: "⛔",
  };
  return (
    <div className={`pipeline-step-circle pipeline-step-${status}`} style={{
      width: 32, height: 32, borderRadius: 16, flexShrink: 0,
      display: "flex", alignItems: "center", justifyContent: "center",
      fontWeight: 600, fontSize: 14, ...styles[status],
      animation: status === "active" ? "stepPulse 1.5s ease-in-out infinite" : undefined,
    }}>
      {glyph[status]}
    </div>
  );
}

/** Click-to-copy step UUID chip. Shows short form + full on hover/click. */
function StepUuidChip({ uuid }: { uuid: string }) {
  const [copied, setCopied] = useState(false);
  function copy(e: React.MouseEvent) {
    e.preventDefault();
    e.stopPropagation();
    // Always show feedback; fall back to legacy execCommand if clipboard API
    // is blocked (e.g. headless test envs, http contexts).
    let ok = false;
    try {
      if (navigator.clipboard?.writeText) {
        void navigator.clipboard.writeText(uuid).catch(() => {});
        ok = true;
      } else {
        const ta = document.createElement("textarea");
        ta.value = uuid;
        document.body.appendChild(ta);
        ta.select();
        ok = document.execCommand("copy");
        document.body.removeChild(ta);
      }
    } catch { /* still flash feedback */ }
    // Browser zeigt bei `userSelect:'all'` + Klick automatisch eine Selektion
    // — die bleibt sichtbar als „markierter Text" stehen, obwohl die Kopie
    // schon im Clipboard ist. Selektion explizit entfernen → keine visuelle
    // Spur, der grüne ✓-Hinweis bleibt das einzige Feedback.
    try { window.getSelection()?.removeAllRanges(); } catch { /* fine */ }
    setCopied(true);
    setTimeout(() => setCopied(false), ok ? 1200 : 800);
  }
  return (
    <code
      onClick={copy}
      title={`Step-UUID: ${uuid}\nKlick zum Kopieren — du kannst diese ID in einer Nachricht referenzieren um genau diesen Schritt anzusprechen`}
      style={{
        fontSize: 12, padding: "3px 9px", borderRadius: 12,
        background: copied ? "#10b981" : "#dbeafe",
        color: copied ? "white" : "#1d4ed8",
        cursor: "pointer", fontFamily: "monospace", fontWeight: 600,
        userSelect: "none",
        border: copied ? "1px solid #10b981" : "1px solid #93c5fd",
        whiteSpace: "nowrap",
        display: "inline-flex", alignItems: "center", gap: 4,
      }}
    >
      <span style={{ fontSize: 10, opacity: 0.7 }}>{copied ? "✓" : "⧉"}</span>
      {copied ? "kopiert" : uuid.slice(0, 8)}
    </code>
  );
}

/** Eine Step-Zeile + rekursiv ihre children (= der nächste „Raum", eingerückt). */
function StepRow({ s, isLast, depth }: { s: PipelineStep; isLast: boolean; depth: number }) {
  return (
    <div className={`pipeline-step pipeline-step-row-${s.status}`}
         style={{ display: "flex", gap: 12, position: "relative", paddingBottom: isLast ? 0 : 12 }}>
      {!isLast && (
        <div style={{
          position: "absolute", left: 15, top: 32, bottom: 0, width: 2,
          background: s.status === "done" ? "#10b981" : "#ddd",
        }} />
      )}
      <StepCircle status={s.status} num={s.num} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
          <strong style={{
            color: s.status === "done" ? "#065f46"
                 : s.status === "active" ? "#1d4ed8"
                 : s.status === "waiting" ? "#b45309"
                 : s.status === "error" ? "#991b1b"
                 : s.status === "blocked" ? "#9ca3af"
                 : "#666",
            fontSize: 14,
          }}>{s.title}</strong>
          {s.optional && <span style={{ fontSize: 11, color: "#999" }}>optional</span>}
          {s.uuid && <StepUuidChip uuid={s.uuid} />}
          {s.duration_ms !== undefined && (
            <span style={{ fontSize: 11, color: "#888", marginLeft: "auto" }}>
              {s.duration_ms < 1000 ? `${s.duration_ms}ms` : `${(s.duration_ms / 1000).toFixed(1)}s`}
            </span>
          )}
        </div>
        <div style={{ fontSize: 12, color: "#666", marginTop: 2 }}>{s.description}</div>
        {s.status === "blocked" && (
          <div style={{ fontSize: 12, marginTop: 4, padding: "4px 8px", background: "#f3f4f6",
                        borderRadius: 3, color: "#6b7280", borderLeft: "3px solid #9ca3af" }}>
            ⛔ blockiert — {s.blockedReason ?? "Vorgänger gestoppt"}
          </div>
        )}
        {s.status === "waiting" && (
          <div style={{ fontSize: 12, marginTop: 4, padding: "4px 8px", background: "#fffbeb",
                        borderRadius: 3, color: "#b45309", borderLeft: "3px solid #f59e0b" }}>
            ✋ wartet auf dich — {s.blockedReason ?? "manuelle Aktion nötig"}
          </div>
        )}
        {s.detail && (
          <div style={{
            fontSize: 12, marginTop: 4, padding: "4px 8px",
            background: s.status === "done" ? "#ecfdf5" : "#f3f4f6",
            borderRadius: 3, color: "#374151",
          }}>
            {s.detail}
          </div>
        )}
        {s.children && s.children.length > 0 && (
          <div style={{ marginTop: 8, marginLeft: 4, paddingLeft: 12, borderLeft: "2px solid #e5e7eb" }}>
            <StepRows steps={s.children} depth={depth + 1} />
          </div>
        )}
      </div>
    </div>
  );
}

/** Rendert eine Step-Liste (eine Schale) — rekursiv via StepRow.children. */
function StepRows({ steps, depth }: { steps: PipelineStep[]; depth: number }) {
  return (
    <>
      {steps.map((s, i) => (
        <StepRow key={s.uuid ?? `${depth}-${s.num}-${i}`} s={s} isLast={i === steps.length - 1} depth={depth} />
      ))}
    </>
  );
}

/** Eine Status-Legende für ALLE Glass-Box-Ansichten (Tabs, Räume, Schritte).
 *  Single Source der Status-Sprache — überall identisch verwenden. */
export const STATUS_LEGEND: Array<{ glyph: string; label: string; color: string }> = [
  { glyph: "✓", label: "fertig / abgeschlossen", color: "#065f46" },
  { glyph: "▶", label: "läuft gerade", color: "#1d4ed8" },
  { glyph: "✋", label: "wartet auf dich (manuelle Aktion)", color: "#b45309" },
  { glyph: "○", label: "noch nicht dran", color: "#9ca3af" },
  { glyph: "✗", label: "Fehler — hängt hier (nicht das Ende!)", color: "#991b1b" },
  { glyph: "🏁", label: "ganzer Prozess abgeschlossen", color: "#065f46" },
];

export function StatusLegend({ compact }: { compact?: boolean } = {}) {
  return (
    <div style={{ fontSize: 11, color: "#6b7280", marginTop: compact ? 8 : 10,
      paddingTop: 8, borderTop: "1px solid #eee", display: "flex", flexWrap: "wrap", gap: 12 }}>
      <strong style={{ color: "#374151" }}>Legende:</strong>
      {STATUS_LEGEND.map((l) => (
        <span key={l.glyph}>
          <span style={{ color: l.color, fontWeight: 700 }}>{l.glyph}</span> {l.label}
        </span>
      ))}
    </div>
  );
}

export function PipelineStepList({ steps }: { steps: PipelineStep[] }) {
  // Sequenzielle Integrität: nach dem ersten error sind Folge-Schritte blocked.
  const stuck = propagateStuck(steps);
  return (
    <div className="pipeline-steps">
      <style>{`
        @keyframes stepPulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(59, 130, 246, 0.4); }
          50%      { box-shadow: 0 0 0 6px rgba(59, 130, 246, 0); }
        }
      `}</style>
      <StepRows steps={stuck} depth={0} />
      <StatusLegend />
    </div>
  );
}

function JobProgress({ job, reads, cellBboxes, cardIdForUuids }: {
  job: JobStatus;
  reads: ReadsState | null;
  cellBboxes: Record<string, any> | null;
  cardIdForUuids?: string;
}) {
  const done = job.status === "done", failed = job.status === "failed";
  const steps = deriveSteps(job, reads, cellBboxes, cardIdForUuids);
  return (
    <div style={{
      padding: 16, marginBottom: 12, borderRadius: 6,
      background: failed ? "#fef2f2" : done ? "#f0fdf4" : "#eff6ff",
      border: `1px solid ${failed ? "#fca5a5" : done ? "#86efac" : "#93c5fd"}`,
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <strong>OCR-Strategie · {done ? "Pipeline fertig" : failed ? "Fehler" : "läuft…"}</strong>
        <span style={{ fontSize: 13, color: "#666" }}>{job.percent}%</span>
      </div>
      <progress value={job.percent} max={100} style={{ width: "100%", marginBottom: 14, height: 6 }} />
      <PipelineStepList steps={steps} />
      <details style={{ marginTop: 12, fontSize: 12 }}>
        <summary style={{ cursor: "pointer", color: "#888" }}>
          Raw Backend-Log ({job.stages_log?.length ?? 0} Events)
        </summary>
        <table style={{ width: "100%", marginTop: 6 }}>
          <tbody>
            {job.stages_log?.map((s, i) => (
              <tr key={i} style={{ borderBottom: "1px solid #f0f0f0" }}>
                <td style={{ padding: "2px 6px", color: "#888", width: 40 }}>{s.percent}</td>
                <td style={{ padding: "2px 6px", fontFamily: "monospace", width: 220 }}>{s.stage}</td>
                <td style={{ padding: "2px 6px" }}>{s.message}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </details>
    </div>
  );
}


function FailedView({ job, jobId }: { job: JobStatus; jobId: string }) {
  const detail = job.error_detail;
  return (
    <div>
      <div style={{ background: "#fee", padding: 12, marginBottom: 12, borderRadius: 4 }}>
        <strong>Fehler: {job.error}</strong>
        {detail && <pre style={{ fontSize: 12, overflow: "auto", marginTop: 8 }}>
          {typeof detail === "string" ? detail : JSON.stringify(detail, null, 2)}
        </pre>}
      </div>
      {(job as any).png_url && (
        <div>
          <h3 style={{ fontSize: 14 }}>Debug-Overlay</h3>
          <p style={{ fontSize: 12, color: "#666" }}>
            Rot = Morphologie h-Lines · Grün = v-Lines · Gelb = Tesseract Anker
          </p>
          <img src={api.admin.testOcrPngUrl(jobId)}
               style={{ maxWidth: "100%", border: "1px solid #ccc" }} alt="debug" />
        </div>
      )}
    </div>
  );
}


function StageBPanel(props: {
  jobId: string; reads: ReadsState; models: ModelInfo[];
  selectedModels: string[]; setSelectedModels: (m: string[]) => void;
  onCellClick: (day: number, field: string) => void;
  runAllCells: () => void;
  cellStatus: string;
  batchProgress: { done: number; total: number } | null;
  activeCell: { day: number; field: string } | null;
  onGtUpload: (file: File) => Promise<void>;
  onGtFromCard: (sourceCardId: string, sourceKind: "timesheet" | "expense") => Promise<void>;
  gtSourceCardId: string | null;
  runActiveCell: (force?: boolean) => Promise<void>;
  resetActiveCell: () => Promise<void>;
  cardIdForUuids?: string;
}) {
  const gtCount = Object.keys(props.reads.gt || {}).length;
  const stageBUuid = props.cardIdForUuids
    ? stepUuid(props.cardIdForUuids, "test-pipeline", "stage-b")
    : undefined;

  return (
    <div style={{ marginTop: 18 }}>
      <h3 style={{ margin: "0 0 10px", display: "flex", alignItems: "center", gap: 8 }}>
        Stufe B — Zell-OCR per Klick
        {stageBUuid && <ReadUuidChip uuid={stageBUuid} />}
      </h3>
      <p style={{ fontSize: 12, color: "#666", lineHeight: 1.5, marginBottom: 12 }}>
        Wähle ein oder mehrere Modelle aus. Klick auf eine Zelle markiert sie (zeigt Details rechts).
        Im Detail-Panel kannst du „Lesen" auslösen oder „Zurücksetzen" — Klicken alleine startet kein OCR.
        „Alle Zellen verarbeiten" jagt die ganze Karte einmal durch alle ausgewählten Modelle.
        Optional: GT-CSV hochladen für Live-Vergleich pro Zelle + Gesamt-Score.
      </p>
      <ModelPicker models={props.models}
                   selected={props.selectedModels}
                   onChange={props.setSelectedModels} />
      <div style={{ display: "flex", gap: 12, alignItems: "center", marginTop: 12, marginBottom: 12, flexWrap: "wrap" }}>
        <button onClick={props.runAllCells}
                disabled={props.selectedModels.length === 0 || props.batchProgress !== null}
                style={{ padding: "6px 14px" }}>
          {props.batchProgress
            ? `Stapel ${props.batchProgress.done}/${props.batchProgress.total}`
            : `Alle ${Object.keys(props.reads.cell_bboxes).length} Zellen verarbeiten (${props.selectedModels.length} Modelle)`}
        </button>
        <GtUploader gtCount={gtCount} onUpload={props.onGtUpload} />
        <GtFromCardPicker onPick={props.onGtFromCard} gtSourceCardId={props.gtSourceCardId} />
        {props.cellStatus && (
          <span style={{ fontSize: 13, color: "#555" }}>
            <strong>Status:</strong> {props.cellStatus}
          </span>
        )}
      </div>
      {props.batchProgress && (
        <progress value={props.batchProgress.done} max={props.batchProgress.total}
                  style={{ width: "100%", height: 6 }} />
      )}
      {gtCount > 0 && (
        <AggregatePanel reads={props.reads} />
      )}
      <ClickableImage jobId={props.jobId} reads={props.reads}
                      onCellClick={props.onCellClick}
                      activeCell={props.activeCell}
                      selectedModels={props.selectedModels}
                      runActiveCell={props.runActiveCell}
                      resetActiveCell={props.resetActiveCell}
                      cardIdForUuids={props.cardIdForUuids} />
      <ReadsTable reads={props.reads} />
    </div>
  );
}


function GtUploader(props: { gtCount: number; onUpload: (file: File) => Promise<void> }) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
      <input ref={inputRef} type="file" accept=".csv,text/csv" style={{ display: "none" }}
             onChange={async (e) => {
               const f = e.target.files?.[0];
               if (!f) return;
               setBusy(true);
               try { await props.onUpload(f); } finally { setBusy(false); }
               if (inputRef.current) inputRef.current.value = "";
             }} />
      <button onClick={() => inputRef.current?.click()} disabled={busy}
              style={{ padding: "6px 14px",
                       background: props.gtCount > 0 ? "#dfd" : "#f4f4f4",
                       border: `1px solid ${props.gtCount > 0 ? "#9c9" : "#ccc"}` }}>
        {busy ? "lädt…" : props.gtCount > 0 ? `GT geladen (${props.gtCount} cells)` : "GT-CSV hochladen"}
      </button>
    </div>
  );
}


/** Picker: lädt GT aus einer existierenden Karte (Werkstatt-Visibility +
 *  Iterations-Vergleich). User wählt eine Karte (z.B. erfolgreich exportierte
 *  April-Karte), Engine bekommt GT als CSV. (2026-05-21) */
function GtFromCardPicker({
  onPick, gtSourceCardId,
}: {
  onPick: (sourceCardId: string, sourceKind: "timesheet" | "expense") => Promise<void>;
  gtSourceCardId: string | null;
}) {
  const [open, setOpen] = useState(false);
  const [cardId, setCardId] = useState("");
  const [busy, setBusy] = useState(false);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <button onClick={() => setOpen(o => !o)} disabled={busy}
              style={{
                padding: "6px 14px",
                background: gtSourceCardId ? "#dfd" : "#f4f4f4",
                border: `1px solid ${gtSourceCardId ? "#9c9" : "#ccc"}`,
              }}
              title="Lade GT aus einer anderen Karte (z.B. erfolgreich exportierten Original) statt CSV-Upload">
        {gtSourceCardId
          ? `GT von Karte ${gtSourceCardId.slice(0, 8)} geladen`
          : "GT von Karte wählen"}
      </button>
      {open && (
        <div style={{ display: "flex", gap: 6, alignItems: "center", marginTop: 4 }}>
          <input
            type="text"
            placeholder="card-id (UUID oder 8-char Präfix)"
            value={cardId}
            onChange={(e) => setCardId(e.target.value.trim())}
            style={{ fontFamily: "monospace", fontSize: 12, padding: "4px 6px", width: 280 }}
          />
          <button disabled={busy || cardId.length < 8} onClick={async () => {
            setBusy(true);
            try {
              await onPick(cardId, "timesheet");
              setOpen(false);
            } finally {
              setBusy(false);
            }
          }} style={{ padding: "4px 10px", fontSize: 12 }}>
            {busy ? "lädt…" : "laden"}
          </button>
          <button onClick={() => setOpen(false)} style={{ padding: "4px 10px", fontSize: 12 }}>×</button>
        </div>
      )}
    </div>
  );
}

function AggregatePanel({ reads }: { reads: ReadsState }) {
  const gt = reads.gt || {};
  const allCellKeys = Object.keys(reads.cell_bboxes);
  // Collect all models that have been used
  const allModels = new Set<string>();
  Object.values(reads.reads).forEach(mr => Object.keys(mr).forEach(m => allModels.add(m)));
  const models = Array.from(allModels).sort();

  if (models.length === 0) {
    return (
      <div style={{ padding: 10, background: "#fffbe6", border: "1px solid #ffe88a",
                    borderRadius: 4, marginBottom: 12, fontSize: 13 }}>
        GT geladen ({Object.keys(gt).length} cells) — klicke Zellen oder „Run all"
        um zu vergleichen.
      </div>
    );
  }

  // Per model: TP / WV / FP / TN / FN
  const stats = models.map(model => {
    let tp = 0, wv = 0, fp = 0, tn = 0, fn = 0, silent = 0;
    for (const cellKey of allCellKeys) {
      const gtVal = (gt[cellKey] || "").trim();
      const r = reads.reads[cellKey]?.[model];
      const engineVal = r?.value;
      const conf = r?.confidence ?? 0;
      const hasGt = gtVal.length > 0;
      const hasEngine = engineVal !== null && engineVal !== undefined && engineVal !== "";
      if (!hasGt && !hasEngine) tn++;
      else if (!hasGt && hasEngine) {
        fp++;
        if (conf >= 0.85) silent++;
      } else if (hasGt && !hasEngine) fn++;
      else {
        if (normTime(engineVal!) === normTime(gtVal)) tp++;
        else {
          wv++;
          if (conf >= 0.85) silent++;
        }
      }
    }
    const total = tp + wv + fp + tn + fn;
    const pop = tp + wv + fn;
    const empty = fp + tn;
    return { model, tp, wv, fp, tn, fn, silent, total, pop, empty };
  });

  return (
    <div style={{ padding: 12, background: "#f0f4ff", border: "1px solid #c4d4ff",
                  borderRadius: 6, marginBottom: 14 }}>
      <h4 style={{ margin: "0 0 8px", fontSize: 14 }}>
        Aggregate vs GT ({Object.keys(gt).length} GT cells)
      </h4>
      <table style={{ width: "100%", fontSize: 13, borderCollapse: "collapse" }}>
        <thead>
          <tr>
            <th style={aggTh}>Modell</th>
            <th style={aggTh}>TP</th>
            <th style={aggTh}>WV</th>
            <th style={aggTh}>FP</th>
            <th style={aggTh}>TN</th>
            <th style={aggTh}>FN</th>
            <th style={aggTh}>Silent</th>
            <th style={aggTh}>TP-Rate (pop)</th>
            <th style={aggTh}>TN-Rate (empty)</th>
          </tr>
        </thead>
        <tbody>
          {stats.map(s => (
            <tr key={s.model}>
              <td style={{ ...aggTd, fontWeight: "bold" }}>{s.model}</td>
              <td style={{ ...aggTd, color: "#0a7" }}>{s.tp}</td>
              <td style={{ ...aggTd, color: "#a70" }}>{s.wv}</td>
              <td style={{ ...aggTd, color: "#a00" }}>{s.fp}</td>
              <td style={{ ...aggTd, color: "#0a7" }}>{s.tn}</td>
              <td style={{ ...aggTd, color: "#a00" }}>{s.fn}</td>
              <td style={{ ...aggTd, color: s.silent > 0 ? "#a00" : "#888", fontWeight: s.silent > 0 ? "bold" : "normal" }}>
                {s.silent}
              </td>
              <td style={aggTd}>{s.pop > 0 ? `${((100 * s.tp) / s.pop).toFixed(1)}%` : "—"}</td>
              <td style={aggTd}>{s.empty > 0 ? `${((100 * s.tn) / s.empty).toFixed(1)}%` : "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p style={{ fontSize: 11, color: "#666", marginTop: 6, marginBottom: 0 }}>
        TP = korrekter Wert · WV = falscher Wert · FP = halluziniert (engine pop, GT empty) ·
        TN = korrekt leer · FN = übersehen (engine empty, GT pop) ·
        <strong> Silent = WV oder FP mit conf ≥ 0.85 (gefährlich!)</strong>
      </p>
    </div>
  );
}


function normTime(s: string): string {
  if (!s) return s;
  const m = s.trim().replace(/\./g, ":").match(/^(\d{1,2}):(\d{2})$/);
  if (m) return `${parseInt(m[1] ?? "0", 10)}:${m[2]}`;
  return s.trim();
}


const aggTh: React.CSSProperties = {
  textAlign: "right", padding: "4px 8px",
  borderBottom: "1px solid #c4d4ff", fontWeight: "bold", color: "#222",
};
const aggTd: React.CSSProperties = {
  textAlign: "right", padding: "4px 8px",
  borderBottom: "1px solid #e8eaf5", fontFamily: "monospace",
};


function ModelPicker({ models, selected, onChange }: {
  models: ModelInfo[]; selected: string[]; onChange: (s: string[]) => void;
}) {
  const toggle = (name: string) => {
    onChange(selected.includes(name)
      ? selected.filter(x => x !== name)
      : [...selected, name]);
  };
  // Build tooltip text combining description + strategy details
  const tip = (m: ModelInfo): string => {
    const lines: string[] = [m.description];
    if (m.strategy) {
      lines.push("");
      lines.push(`Strategy ${m.strategy}:`);
      if (m.strategy_description) lines.push(m.strategy_description);
    }
    return lines.join("\n");
  };
  return (
    <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
      {models.map(m => (
        <label key={m.name} title={tip(m)}
               style={{
                 display: "flex", alignItems: "center", gap: 6,
                 padding: "6px 10px",
                 background: selected.includes(m.name) ? "#dfd" : "#f4f4f4",
                 border: `1px solid ${selected.includes(m.name) ? "#9c9" : "#ccc"}`,
                 borderRadius: 4, cursor: "pointer", fontSize: 13,
                 opacity: m.loaded ? 1 : 0.7,
               }}>
          <input type="checkbox" checked={selected.includes(m.name)}
                 onChange={() => toggle(m.name)} />
          {m.label}
          {m.strategy && (
            <span style={{
              fontSize: 10, fontWeight: 600, padding: "2px 6px",
              background: "#2563eb", color: "#fff", borderRadius: 3,
            }} title={m.strategy_description || ""}>
              {m.strategy}
            </span>
          )}
          {!m.loaded && <span style={{ fontSize: 11, color: "#888" }}>(setup pending)</span>}
        </label>
      ))}
    </div>
  );
}


function ClickableImage(props: {
  jobId: string; reads: ReadsState;
  onCellClick: (day: number, field: string) => void;
  activeCell: { day: number; field: string } | null;
  selectedModels: string[];
  runActiveCell?: (force?: boolean) => Promise<void>;
  resetActiveCell?: () => Promise<void>;
  cardIdForUuids?: string;
}) {
  const imgRef = useRef<HTMLImageElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [displaySize, setDisplaySize] = useState<{ w: number; h: number } | null>(null);
  const [hoveredCellKey, setHoveredCellKey] = useState<string | null>(null);
  const [mousePos, setMousePos] = useState<{ x: number; y: number } | null>(null);

  useEffect(() => {
    if (imgRef.current) {
      const r = imgRef.current.getBoundingClientRect();
      setDisplaySize({ w: r.width, h: r.height });
    }
  }, [props.reads]);

  // Layout: side-by-side — image left, details right (always-visible
  // detail panel for the hovered/active cell). PLUS a floating loupe
  // anchored to mouse for instant scan.
  return (
    <div style={{ display: "flex", gap: 16, marginBottom: 14 }}>
      <div ref={containerRef}
           style={{ position: "relative", display: "inline-block", flex: "1 1 auto", maxWidth: "calc(100% - 320px)" }}>
        <img ref={imgRef}
             src={api.admin.testOcrPngUrl(props.jobId)}
             onLoad={() => {
               if (imgRef.current) {
                 const r = imgRef.current.getBoundingClientRect();
                 setDisplaySize({ w: r.width, h: r.height });
               }
             }}
             style={{ maxWidth: "100%", border: "1px solid #ccc", display: "block" }} alt="grid" />
        {displaySize && (
          <svg style={{ position: "absolute", top: 0, left: 0, pointerEvents: "none" }}
               width={displaySize.w} height={displaySize.h}>
            {Object.values(props.reads.cell_bboxes).map(b => {
              const cellKey = `d${String(b.day).padStart(2, "0")}_${b.field}`;
              const cellReads = props.reads.reads[cellKey];
              const active = props.activeCell?.day === b.day && props.activeCell?.field === b.field;
              const hovered = hoveredCellKey === cellKey;
              const x = b.leftPct * displaySize.w;
              const y = b.topPct * displaySize.h;
              const w = (b.rightPct - b.leftPct) * displaySize.w;
              const h = (b.bottomPct - b.topPct) * displaySize.h;
              const hasReads = cellReads && Object.keys(cellReads).length > 0;
              const bestRead = hasReads ? Object.values(cellReads)[0] : null;
              const stroke = active ? "#3399ff" :
                             hovered ? "#ff9900" :
                             bestRead ? (bestRead.confidence >= 0.85 ? "#22cc22"
                                         : bestRead.confidence >= 0.4 ? "#ffaa00"
                                         : "#cc2222")
                                      : "rgba(120,120,200,0.3)";
              return (
                <g key={cellKey}>
                  <rect x={x} y={y} width={w} height={h}
                        fill={hasReads ? "rgba(255,255,255,0.05)" : "transparent"}
                        stroke={stroke}
                        strokeWidth={hovered || active ? 2.5 : hasReads ? 1.5 : 0.5}
                        style={{ pointerEvents: "all", cursor: "pointer" }}
                        onClick={() => props.onCellClick(b.day, b.field)}
                        onMouseEnter={() => setHoveredCellKey(cellKey)}
                        onMouseMove={(e) => {
                          const rect = containerRef.current?.getBoundingClientRect();
                          if (rect) setMousePos({ x: e.clientX - rect.left, y: e.clientY - rect.top });
                        }}
                        onMouseLeave={() => { setHoveredCellKey(null); setMousePos(null); }} />
                </g>
              );
            })}
          </svg>
        )}
        {/* Floating loupe near mouse — magnified cell + readable values */}
        {hoveredCellKey && mousePos && displaySize && (() => {
          const cell = Object.values(props.reads.cell_bboxes).find(b =>
            `d${String(b.day).padStart(2, "0")}_${b.field}` === hoveredCellKey);
          if (!cell) return null;
          return (
            <Loupe jobId={props.jobId}
                   cell={cell}
                   reads={props.reads.reads[hoveredCellKey]}
                   gt={props.reads.gt?.[hoveredCellKey]}
                   displaySize={displaySize}
                   mousePos={mousePos}
                   imageW={props.reads.image_w}
                   imageH={props.reads.image_h} />
          );
        })()}
      </div>
      {/* Always-visible Detail Panel for active cell */}
      <CellDetailPanel jobId={props.jobId}
                       reads={props.reads}
                       activeCell={props.activeCell}
                       selectedModels={props.selectedModels}
                       runActiveCell={props.runActiveCell}
                       resetActiveCell={props.resetActiveCell}
                       cardIdForUuids={props.cardIdForUuids} />
    </div>
  );
}


function Loupe(props: {
  jobId: string;
  cell: { day: number; field: string; leftPct: number; topPct: number; rightPct: number; bottomPct: number };
  reads: Record<string, any> | undefined;
  gt: string | undefined;
  displaySize: { w: number; h: number };
  mousePos: { x: number; y: number };
  imageW: number; imageH: number;
}) {
  // Position loupe near mouse but offset so it doesn't cover the cell.
  // Flip to left side if mouse near right edge.
  const LOUPE_W = 340, LOUPE_H = 240;
  const offset = 16;
  let left = props.mousePos.x + offset;
  let top = props.mousePos.y + offset;
  if (left + LOUPE_W > props.displaySize.w) left = props.mousePos.x - LOUPE_W - offset;
  if (top + LOUPE_H > props.displaySize.h) top = props.mousePos.y - LOUPE_H - offset;
  if (left < 0) left = 0;
  if (top < 0) top = 0;

  // Crop coords for zoom — use percentage rectangle on the source PNG.
  // We do this via SVG <image> with viewBox to scale the chosen region.
  const cropX = props.cell.leftPct * props.imageW;
  const cropY = props.cell.topPct * props.imageH;
  const cropW = (props.cell.rightPct - props.cell.leftPct) * props.imageW;
  const cropH = (props.cell.bottomPct - props.cell.topPct) * props.imageH;
  // Add 30% padding so loupe shows context around the cell
  const padX = cropW * 0.3, padY = cropH * 0.3;
  const vbX = Math.max(0, cropX - padX);
  const vbY = Math.max(0, cropY - padY);
  const vbW = cropW + 2 * padX;
  const vbH = cropH + 2 * padY;

  const reads = props.reads || {};
  const cellKey = `d${String(props.cell.day).padStart(2, "0")}_${props.cell.field}`;

  return (
    <div style={{
      position: "absolute", left, top,
      width: LOUPE_W, minHeight: LOUPE_H,
      background: "white",
      border: "2px solid #3399ff",
      borderRadius: 6,
      boxShadow: "0 4px 16px rgba(0,0,0,0.2)",
      pointerEvents: "none",
      zIndex: 1000,
      fontFamily: "system-ui, sans-serif",
    }}>
      <div style={{ padding: "6px 10px", background: "#3399ff", color: "white",
                    fontSize: 13, fontWeight: "bold", borderRadius: "4px 4px 0 0" }}>
        {cellKey}
      </div>
      <svg width={LOUPE_W} height={140} viewBox={`${vbX} ${vbY} ${vbW} ${vbH}`}
           preserveAspectRatio="xMidYMid meet"
           style={{ background: "#f8f8f8" }}>
        <image href={api.admin.testOcrPngUrl(props.jobId)}
               x={0} y={0} width={props.imageW} height={props.imageH} />
      </svg>
      <div style={{ padding: "8px 10px" }}>
        {Object.keys(reads).length === 0 ? (
          <div style={{ fontSize: 12, color: "#888" }}>
            <em>Klick auf Zelle, dann „Lesen" im Detail-Panel</em>
          </div>
        ) : (
          <table style={{ width: "100%", fontSize: 14 }}>
            <tbody>
              {Object.entries(reads).map(([model, r]) => {
                const conf = r.confidence;
                const color = conf >= 0.85 ? "#0a7" : conf >= 0.4 ? "#a70" : "#a00";
                return (
                  <tr key={model}>
                    <td style={{ padding: "2px 6px", fontWeight: "bold", color: "#555", fontSize: 11 }}>
                      {model.toUpperCase()}
                    </td>
                    <td style={{ padding: "2px 6px", color, fontFamily: "monospace", fontSize: 16, fontWeight: "bold" }}>
                      {r.value ?? (r.error ? "✗" : "—")}
                    </td>
                    <td style={{ padding: "2px 6px", color: "#888", fontSize: 11 }}>
                      {(r.confidence * 100).toFixed(0)}%
                    </td>
                  </tr>
                );
              })}
              {props.gt && (
                <tr style={{ borderTop: "1px solid #eee" }}>
                  <td style={{ padding: "2px 6px", fontSize: 11, color: "#555" }}>GT</td>
                  <td style={{ padding: "2px 6px", fontFamily: "monospace", fontSize: 14, fontWeight: "bold" }}>
                    {props.gt}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}


function CellDetailPanel(props: {
  jobId: string;
  reads: ReadsState;
  activeCell: { day: number; field: string } | null;
  selectedModels?: string[];
  runActiveCell?: (force?: boolean) => Promise<void>;
  resetActiveCell?: () => Promise<void>;
  cardIdForUuids?: string;
}) {
  if (!props.activeCell) {
    return (
      <div style={{ width: 300, padding: 16, background: "#fafafa",
                    border: "1px dashed #ccc", borderRadius: 6,
                    fontSize: 13, color: "#888" }}>
        <h4 style={{ margin: "0 0 8px", fontSize: 14, color: "#333" }}>Zell-Details</h4>
        Klicke eine Zelle im Bild um sie auszuwählen.
      </div>
    );
  }
  const cellKey = `d${String(props.activeCell.day).padStart(2, "0")}_${props.activeCell.field}`;
  const bbox = props.reads.cell_bboxes[cellKey];
  const reads = props.reads.reads[cellKey] || {};
  const gt = props.reads.gt?.[cellKey];
  const selected = props.selectedModels ?? [];
  const haveAllSelected = selected.length > 0 && selected.every(m => reads[m]);
  return (
    <div style={{ width: 300, padding: 14, background: "white",
                  border: "1px solid #3399ff", borderRadius: 6 }}>
      <h4 style={{ margin: "0 0 8px", fontSize: 14, color: "#3399ff" }}>
        {cellKey} (Tag {props.activeCell.day})
      </h4>
      {bbox && (
        <svg viewBox={`${bbox.x1 - 20} ${bbox.y1 - 10} ${bbox.x2 - bbox.x1 + 40} ${bbox.y2 - bbox.y1 + 20}`}
             style={{ width: "100%", height: 100, background: "#f8f8f8",
                      border: "1px solid #eee", marginBottom: 10 }}>
          <image href={api.admin.testOcrPngUrl(props.jobId)}
                 x={0} y={0}
                 width={props.reads.image_w} height={props.reads.image_h} />
        </svg>
      )}
      {/* Action buttons */}
      {props.runActiveCell && (
        <div style={{ display: "flex", gap: 6, marginBottom: 10, flexWrap: "wrap" }}>
          {selected.length === 0 ? (
            <span style={{ fontSize: 11, color: "#a70" }}>
              Wähle oben ein Modell aus.
            </span>
          ) : !haveAllSelected ? (
            <button onClick={() => props.runActiveCell!(false)}
                    style={{ padding: "6px 12px", fontSize: 12,
                             background: "#3399ff", color: "white",
                             border: "none", borderRadius: 4, cursor: "pointer", fontWeight: "bold" }}>
              ▶ Lesen ({selected.filter(m => !reads[m]).join(", ")})
            </button>
          ) : (
            <button onClick={() => props.runActiveCell!(true)}
                    style={{ padding: "6px 12px", fontSize: 12,
                             background: "#fff", color: "#3399ff",
                             border: "1px solid #3399ff", borderRadius: 4, cursor: "pointer" }}>
              ↻ Erneut lesen
            </button>
          )}
          {Object.keys(reads).length > 0 && props.resetActiveCell && (
            <button onClick={() => props.resetActiveCell!()}
                    style={{ padding: "6px 12px", fontSize: 12,
                             background: "#fff", color: "#a00",
                             border: "1px solid #faa", borderRadius: 4, cursor: "pointer" }}>
              ⌧ Zurücksetzen
            </button>
          )}
        </div>
      )}
      {Object.keys(reads).length === 0 ? (
        <p style={{ fontSize: 13, color: "#888" }}>
          <em>Noch nicht gelesen. Klick „Lesen" oben.</em>
        </p>
      ) : (
        <table style={{ width: "100%", fontSize: 14 }}>
          <thead>
            <tr><th style={{ textAlign: "left", padding: "2px 4px", fontSize: 11, color: "#888" }}>Modell</th>
                <th style={{ textAlign: "left", padding: "2px 4px", fontSize: 11, color: "#888" }}>Wert</th>
                <th style={{ textAlign: "right", padding: "2px 4px", fontSize: 11, color: "#888" }}>Konf. · Zeit</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(reads).map(([model, r]) => {
              const conf = r.confidence;
              const color = conf >= 0.85 ? "#0a7" : conf >= 0.4 ? "#a70" : "#a00";
              // Per-Read deterministic UUID — same (cardId, cell, model) →
              // same UUID. Lets the user reference exactly one model-read in
              // conversation: "qwen3 read at <uuid>".
              const readUuid = props.cardIdForUuids
                ? stepUuid(props.cardIdForUuids, "test-read", `${cellKey}|${model}`)
                : undefined;
              // Glass-Box: wenn die Engine etwas geliefert hat, das aber von der
              // Strategy gefiltert wurde (z.B. K3 confidence < 0.85), zeigen wir
              // den raw_output mit „(gefiltert)"-Marker statt „—". So sieht der
              // User was die Engine wirklich gelesen hat, und kann entscheiden
              // ob die Schwelle zu strikt ist.
              const filteredLowConf = (r as any).filtered_low_conf === true;
              const rawOutput = (r as any).raw_output ?? (r as any).raw_response ?? null;
              const strategy = (r as any).strategy as string | undefined;
              const displayValue = r.value
                ?? (filteredLowConf && rawOutput
                    ? <span style={{ color: "#a70", fontWeight: 400 }}>{rawOutput} <span style={{ fontSize: 11 }}>(gefiltert)</span></span>
                    : r.error ? "✗" : "—");
              return (
                <tr key={model} style={{ borderTop: "1px solid #f0f0f0" }}>
                  <td style={{ padding: "4px", fontFamily: "monospace", fontSize: 12 }}>
                    <div>
                      {model}
                      {strategy && <span style={{ marginLeft: 4, fontSize: 10, background: "#e0e7ff", color: "#3730a3", padding: "1px 4px", borderRadius: 3 }}>{strategy}</span>}
                    </div>
                    {readUuid && (
                      <ReadUuidChip uuid={readUuid} />
                    )}
                  </td>
                  <td style={{ padding: "4px", color, fontFamily: "monospace", fontSize: 18, fontWeight: "bold" }}
                      title={rawOutput && r.value !== rawOutput ? `raw: ${rawOutput}` : undefined}>
                    {displayValue}
                  </td>
                  <td style={{ padding: "4px", textAlign: "right", color: "#888", fontSize: 11 }}>
                    {(r.confidence * 100).toFixed(0)}% · {r.latency_ms}ms
                  </td>
                </tr>
              );
            })}
            {gt && (
              <tr style={{ borderTop: "2px solid #ccc" }}>
                <td style={{ padding: "4px", fontWeight: "bold" }}>GT</td>
                <td style={{ padding: "4px", fontFamily: "monospace", fontSize: 18, fontWeight: "bold" }}>{gt}</td>
                <td></td>
              </tr>
            )}
          </tbody>
        </table>
      )}
    </div>
  );
}


function ReadUuidChip({ uuid }: { uuid: string }) {
  const [copied, setCopied] = useState(false);
  const onCopy = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(uuid);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch { /* clipboard blocked — silent */ }
  };
  return (
    <span
      onClick={onCopy}
      title={`Read-UUID kopieren: ${uuid}`}
      style={{
        display: "inline-block", marginTop: 2, padding: "1px 5px",
        background: copied ? "#dcfce7" : "#f1f5f9",
        border: `1px solid ${copied ? "#86efac" : "#e2e8f0"}`,
        borderRadius: 3, fontSize: 10, fontFamily: "monospace",
        color: "#475569", cursor: "pointer", userSelect: "all",
      }}
    >
      {copied ? "✓ " : ""}{stepUuidShort(uuid)}
    </span>
  );
}

function ReadsTable(props: { reads: ReadsState }) {
  const cellsWithReads = Object.entries(props.reads.reads)
    .filter(([_, modelReads]) => Object.keys(modelReads).length > 0)
    .sort();
  if (cellsWithReads.length === 0) {
    return (
      <p style={{ fontSize: 13, color: "#999", marginTop: 12 }}>
        Noch keine Zellen gelesen. Klick auf eine Zelle und „Lesen" — oder „Alle Zellen verarbeiten".
      </p>
    );
  }
  // Build column list of models used
  const allModels = new Set<string>();
  cellsWithReads.forEach(([_, mr]) => Object.keys(mr).forEach(m => allModels.add(m)));
  const modelCols = Array.from(allModels).sort();

  return (
    <div style={{ marginTop: 14 }}>
      <h4 style={{ margin: "8px 0", fontSize: 14 }}>Gelesene Zellen ({cellsWithReads.length})</h4>
      <div style={{ maxHeight: 360, overflow: "auto", border: "1px solid #ddd" }}>
        <table style={{ width: "100%", fontSize: 12, borderCollapse: "collapse" }}>
          <thead style={{ position: "sticky", top: 0, background: "#fafafa" }}>
            <tr>
              <th style={{ textAlign: "left", padding: "4px 8px", borderBottom: "1px solid #ccc" }}>Zelle</th>
              {modelCols.map(m => (
                <th key={m} style={{ textAlign: "left", padding: "4px 8px", borderBottom: "1px solid #ccc" }}>
                  {m}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {cellsWithReads.map(([cellKey, mr]) => (
              <tr key={cellKey} style={{ borderBottom: "1px solid #f0f0f0" }}>
                <td style={{ padding: "4px 8px", fontFamily: "monospace" }}>{cellKey}</td>
                {modelCols.map(m => {
                  const r = mr[m];
                  if (!r) return <td key={m} style={{ padding: "4px 8px", color: "#bbb" }}>—</td>;
                  const color = r.confidence >= 0.85 ? "#0a7" : r.confidence >= 0.4 ? "#a70" : r.error ? "#a00" : "#a00";
                  return (
                    <td key={m} style={{ padding: "4px 8px", color }}>
                      <span style={{ fontFamily: "monospace" }}>{r.value ?? (r.error ? "✗" : "—")}</span>
                      <span style={{ color: "#888", fontSize: 11, marginLeft: 6 }}>
                        {r.confidence.toFixed(2)} · {r.latency_ms}ms
                      </span>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
